import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Trash, Users, Buildings, Pencil, Clock } from "@phosphor-icons/react";
import { FEATURES, DEFAULT_ATTENDANT_PERMISSIONS } from "@/lib/features";

const SECTOR_COLORS = ["#0F4C3A", "#2563EB", "#D97706", "#DC2626", "#7C3AED", "#0891B2"];

export default function CompanyTeam() {
  const [sectors, setSectors] = useState([]);
  const [attendants, setAttendants] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [openSector, setOpenSector] = useState(false);
  const [openAttendant, setOpenAttendant] = useState(false);
  const [sectorForm, setSectorForm] = useState({ name: "", color: SECTOR_COLORS[0] });
  const [attForm, setAttForm] = useState({ name: "", email: "", password: "", sector_ids: [], work_schedule_id: "", permissions: [...DEFAULT_ATTENDANT_PERMISSIONS] });
  const [editingAtt, setEditingAtt] = useState(null); // {id, name, sector_ids, work_schedule_id, permissions}

  const load = async () => {
    const [s, a, w] = await Promise.all([
      api.get("/company/sectors"),
      api.get("/company/attendants"),
      api.get("/work-schedules").catch(() => ({ data: [] })),
    ]);
    setSectors(s.data);
    setAttendants(a.data);
    setSchedules(w.data || []);
  };
  useEffect(() => { load(); }, []);

  const saveSector = async () => {
    try {
      await api.post("/company/sectors", sectorForm);
      toast.success("Setor criado");
      setOpenSector(false);
      setSectorForm({ name: "", color: SECTOR_COLORS[0] });
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const deleteSector = async (s) => {
    if (!window.confirm(`Excluir setor "${s.name}"?`)) return;
    try {
      await api.delete(`/company/sectors/${s.id}`);
      toast.success("Setor removido");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const saveAttendant = async () => {
    try {
      await api.post("/company/attendants", {
        name: attForm.name,
        email: attForm.email,
        password: attForm.password,
        sector_ids: attForm.sector_ids,
        work_schedule_id: attForm.work_schedule_id || null,
        permissions: attForm.permissions,
      });
      toast.success("Atendente adicionado");
      setOpenAttendant(false);
      setAttForm({ name: "", email: "", password: "", sector_ids: [], work_schedule_id: "", permissions: [...DEFAULT_ATTENDANT_PERMISSIONS] });
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const saveEdit = async () => {
    try {
      await api.put(`/company/attendants/${editingAtt.id}`, {
        name: editingAtt.name,
        sector_ids: editingAtt.sector_ids,
        work_schedule_id: editingAtt.work_schedule_id || "",
        permissions: editingAtt.permissions,
      });
      toast.success("Atendente atualizado");
      setEditingAtt(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const toggleAttPermission = (key) => {
    setAttForm((prev) => ({
      ...prev,
      permissions: prev.permissions.includes(key)
        ? prev.permissions.filter((k) => k !== key)
        : [...prev.permissions, key],
    }));
  };
  const toggleAllPermissions = () => {
    setAttForm((prev) => ({
      ...prev,
      permissions: prev.permissions.length === FEATURES.length ? [] : FEATURES.map((f) => f.key),
    }));
  };
  const toggleEditPermission = (key) => {
    setEditingAtt((prev) => ({
      ...prev,
      permissions: (prev.permissions || []).includes(key)
        ? prev.permissions.filter((k) => k !== key)
        : [...(prev.permissions || []), key],
    }));
  };
  const toggleAllEditPermissions = () => {
    setEditingAtt((prev) => ({
      ...prev,
      permissions: (prev.permissions || []).length === FEATURES.length ? [] : FEATURES.map((f) => f.key),
    }));
  };

  const toggleAttSector = (id) => {
    setAttForm((prev) => ({
      ...prev,
      sector_ids: prev.sector_ids.includes(id)
        ? prev.sector_ids.filter((x) => x !== id)
        : [...prev.sector_ids, id],
    }));
  };

  const toggleAllSectors = () => {
    setAttForm((prev) => ({
      ...prev,
      sector_ids: prev.sector_ids.length === sectors.length ? [] : sectors.map((s) => s.id),
    }));
  };

  const deleteAttendant = async (u) => {
    if (u.role === "company_owner") {
      toast.error("Não é possível remover o dono");
      return;
    }
    if (!window.confirm(`Remover atendente ${u.name}?`)) return;
    try {
      await api.delete(`/company/attendants/${u.id}`);
      toast.success("Atendente removido");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="p-8 max-w-6xl">
      <div className="mb-8">
        <p className="text-xs font-semibold tracking-[0.3em] uppercase text-[#0F4C3A]">Gestão</p>
        <h1 className="text-4xl font-black tracking-tighter text-[#1A1A1A] mt-1">Equipe</h1>
        <p className="text-sm text-[#5C5C59] mt-2">Setores e atendentes que atendem os tickets da sua empresa.</p>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {/* Sectors */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Buildings size={20} weight="duotone" className="text-[#0F4C3A]" />
              <h2 className="text-xl font-bold tracking-tight">Setores</h2>
            </div>
            <Dialog open={openSector} onOpenChange={setOpenSector}>
              <DialogTrigger asChild>
                <Button data-testid="new-sector-btn" size="sm" className="bg-[#0F4C3A] hover:bg-[#0A3629] text-white rounded-none">
                  <Plus size={14} weight="bold" className="mr-1" /> Novo setor
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-white rounded-none border-[#E5E5E0]">
                <DialogHeader><DialogTitle>Novo setor</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  <div>
                    <Label className="text-xs uppercase tracking-wider font-semibold">Nome</Label>
                    <Input data-testid="sector-name-input" value={sectorForm.name} onChange={(e) => setSectorForm({ ...sectorForm, name: e.target.value })} className="rounded-none mt-1" placeholder="Ex: Suporte, Financeiro" />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider font-semibold">Cor</Label>
                    <div className="flex gap-2 mt-1">
                      {SECTOR_COLORS.map((c) => (
                        <button key={c} type="button" onClick={() => setSectorForm({ ...sectorForm, color: c })}
                          data-testid={`sector-color-${c}`}
                          className={`h-8 w-8 border-2 transition-all ${sectorForm.color === c ? "border-[#1A1A1A] scale-110" : "border-transparent"}`}
                          style={{ background: c }} />
                      ))}
                    </div>
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="ghost" onClick={() => setOpenSector(false)} className="rounded-none">Cancelar</Button>
                  <Button data-testid="sector-save-btn" onClick={saveSector} className="bg-[#0F4C3A] hover:bg-[#0A3629] text-white rounded-none">Criar</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>

          <div className="bg-white border border-[#E5E5E0] shadow-brutal" data-testid="sectors-list">
            {sectors.length === 0 && (
              <div className="p-6 text-center text-xs text-[#5C5C59]">Nenhum setor. Crie setores para organizar sua equipe.</div>
            )}
            {sectors.map((s) => (
              <div key={s.id} className="flex items-center justify-between p-3 border-b border-[#E5E5E0] last:border-b-0">
                <div className="flex items-center gap-3">
                  <div className="h-4 w-4" style={{ background: s.color }} />
                  <span className="font-semibold text-sm">{s.name}</span>
                </div>
                <button onClick={() => deleteSector(s)} data-testid={`delete-sector-${s.id}`} className="p-1 text-[#DC2626] hover:bg-[#F7F7F5] transition-colors">
                  <Trash size={16} />
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Attendants */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Users size={20} weight="duotone" className="text-[#0F4C3A]" />
              <h2 className="text-xl font-bold tracking-tight">Atendentes</h2>
            </div>
            <Dialog open={openAttendant} onOpenChange={setOpenAttendant}>
              <DialogTrigger asChild>
                <Button data-testid="new-attendant-btn" size="sm" className="bg-[#0F4C3A] hover:bg-[#0A3629] text-white rounded-none">
                  <Plus size={14} weight="bold" className="mr-1" /> Novo atendente
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-white rounded-none border-[#E5E5E0]">
                <DialogHeader><DialogTitle>Novo atendente</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  <div>
                    <Label className="text-xs uppercase tracking-wider font-semibold">Nome</Label>
                    <Input data-testid="attendant-name-input" value={attForm.name} onChange={(e) => setAttForm({ ...attForm, name: e.target.value })} className="rounded-none mt-1" />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider font-semibold">E-mail</Label>
                    <Input data-testid="attendant-email-input" type="email" value={attForm.email} onChange={(e) => setAttForm({ ...attForm, email: e.target.value })} className="rounded-none mt-1" />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider font-semibold">Senha inicial</Label>
                    <Input data-testid="attendant-password-input" value={attForm.password} onChange={(e) => setAttForm({ ...attForm, password: e.target.value })} className="rounded-none mt-1" />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider font-semibold">Setores</Label>
                    <p className="text-[10px] text-[#5C5C59] mt-0.5">
                      Marque os setores que este atendente vai atender. Deixe em branco pra qualquer setor.
                    </p>
                    <div className="mt-2 border border-[#E5E5E0] max-h-48 overflow-y-auto" data-testid="attendant-sectors-list">
                      {sectors.length === 0 ? (
                        <div className="p-3 text-xs text-[#5C5C59] text-center">Nenhum setor cadastrado.</div>
                      ) : (
                        <>
                          <label className="flex items-center gap-2 px-3 py-2 border-b border-[#E5E5E0] bg-[#F7F7F5] cursor-pointer hover:bg-[#EDF3F0]">
                            <input
                              type="checkbox"
                              checked={attForm.sector_ids.length === sectors.length && sectors.length > 0}
                              onChange={toggleAllSectors}
                              data-testid="attendant-all-sectors"
                              className="accent-[#0F4C3A]"
                            />
                            <span className="text-xs font-bold uppercase tracking-wider">Todos os setores</span>
                          </label>
                          {sectors.map((s) => (
                            <label key={s.id} className="flex items-center gap-2 px-3 py-2 border-b border-[#E5E5E0] last:border-b-0 cursor-pointer hover:bg-[#F7F7F5]">
                              <input
                                type="checkbox"
                                checked={attForm.sector_ids.includes(s.id)}
                                onChange={() => toggleAttSector(s.id)}
                                data-testid={`attendant-sector-${s.id}`}
                                className="accent-[#0F4C3A]"
                              />
                              <span className="inline-block w-2 h-2 rounded-full" style={{ background: s.color || "#0F4C3A" }} />
                              <span className="text-sm">{s.name}</span>
                            </label>
                          ))}
                        </>
                      )}
                    </div>
                    {attForm.sector_ids.length > 0 && (
                      <p className="text-[10px] text-[#0F4C3A] mt-1 font-semibold">
                        {attForm.sector_ids.length} setor(es) selecionado(s)
                      </p>
                    )}
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider font-semibold">Jornada de Trabalho</Label>
                    <p className="text-[10px] text-[#5C5C59] mt-0.5">
                      Restringe o login a horários específicos. Deixe em branco para acesso 24/7.
                    </p>
                    <Select
                      value={attForm.work_schedule_id || "__none__"}
                      onValueChange={(v) => setAttForm({ ...attForm, work_schedule_id: v === "__none__" ? "" : v })}
                    >
                      <SelectTrigger data-testid="attendant-workschedule-select" className="rounded-none mt-1 bg-white">
                        <SelectValue placeholder="Sem restrição de horário" />
                      </SelectTrigger>
                      <SelectContent className="bg-white rounded-none">
                        <SelectItem value="__none__">Sem restrição de horário</SelectItem>
                        {schedules.map((w) => (
                          <SelectItem key={w.id} value={w.id}>{w.name}{!w.active && " (inativa)"}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Permissions checkbox list */}
                  <div>
                    <Label className="text-xs uppercase tracking-wider font-semibold">Permissões de acesso</Label>
                    <p className="text-[10px] text-[#5C5C59] mt-0.5">
                      Marque tudo que o atendente vai enxergar no painel. O padrão é o essencial pra atender.
                    </p>
                    <div className="mt-2 border border-[#E5E5E0] max-h-56 overflow-y-auto" data-testid="attendant-permissions-list">
                      <label className="flex items-center gap-2 px-3 py-2 border-b border-[#E5E5E0] bg-[#F7F7F5] cursor-pointer hover:bg-[#EDF3F0]">
                        <input
                          type="checkbox"
                          checked={attForm.permissions.length === FEATURES.length}
                          onChange={toggleAllPermissions}
                          data-testid="attendant-all-permissions"
                          className="accent-[#0F4C3A]"
                        />
                        <span className="text-xs font-bold uppercase tracking-wider">Marcar todas</span>
                      </label>
                      {FEATURES.map((f) => (
                        <label key={f.key} className="flex items-center gap-2 px-3 py-2 border-b border-[#E5E5E0] last:border-b-0 cursor-pointer hover:bg-[#F7F7F5]">
                          <input
                            type="checkbox"
                            checked={attForm.permissions.includes(f.key)}
                            onChange={() => toggleAttPermission(f.key)}
                            data-testid={`attendant-perm-${f.key}`}
                            className="accent-[#0F4C3A]"
                          />
                          <span className="text-sm">{f.label}</span>
                        </label>
                      ))}
                    </div>
                    <p className="text-[10px] text-[#0F4C3A] mt-1 font-semibold">
                      {attForm.permissions.length} de {FEATURES.length} liberado(s)
                    </p>
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="ghost" onClick={() => setOpenAttendant(false)} className="rounded-none">Cancelar</Button>
                  <Button data-testid="attendant-save-btn" onClick={saveAttendant} className="bg-[#0F4C3A] hover:bg-[#0A3629] text-white rounded-none">Criar</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>

          <div className="bg-white border border-[#E5E5E0] shadow-brutal" data-testid="attendants-list">
            {attendants.length === 0 && (
              <div className="p-6 text-center text-xs text-[#5C5C59]">Nenhum atendente.</div>
            )}
            {attendants.map((u) => (
              <div key={u.id} className="flex items-center justify-between p-3 border-b border-[#E5E5E0] last:border-b-0">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="h-9 w-9 bg-[#0F4C3A] text-white flex items-center justify-center text-xs font-bold shrink-0">
                    {u.name.split(" ").map((n) => n[0]).slice(0, 2).join("")}
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm font-semibold truncate">{u.name}
                      {u.role === "company_owner" && (
                        <span className="ml-2 text-[9px] uppercase tracking-widest font-semibold text-[#0F4C3A] bg-[#EDF3F0] px-1.5 py-0.5">Dono</span>
                      )}
                    </div>
                    <div className="text-[10px] font-mono text-[#5C5C59] truncate">{u.email}</div>
                    {(u.sector_names && u.sector_names.length > 0) ? (
                      <div className="text-[10px] text-[#5C5C59] truncate">
                        {u.sector_names.length === sectors.length && sectors.length > 0
                          ? "Setores: Todos"
                          : `Setores: ${u.sector_names.join(", ")}`}
                      </div>
                    ) : u.sector_name ? (
                      <div className="text-[10px] text-[#5C5C59]">Setor: {u.sector_name}</div>
                    ) : null}
                    {u.work_schedule_name && (
                      <div className="text-[10px] text-[#0F4C3A] flex items-center gap-1 mt-0.5">
                        <Clock size={10} weight="bold" /> Jornada: {u.work_schedule_name}
                      </div>
                    )}
                  </div>
                </div>
                {u.role !== "company_owner" && (
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => setEditingAtt({
                        id: u.id, name: u.name,
                        sector_ids: [...(u.sector_ids || [])],
                        work_schedule_id: u.work_schedule_id || "",
                        permissions: [...(u.permissions || DEFAULT_ATTENDANT_PERMISSIONS)],
                      })}
                      data-testid={`edit-attendant-${u.id}`}
                      className="p-1 text-[#0F4C3A] hover:bg-[#EDF3F0] transition-colors"
                    >
                      <Pencil size={16} />
                    </button>
                    <button onClick={() => deleteAttendant(u)} data-testid={`delete-attendant-${u.id}`} className="p-1 text-[#DC2626] hover:bg-[#F7F7F5] transition-colors">
                      <Trash size={16} />
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Edit attendant dialog */}
      <Dialog open={!!editingAtt} onOpenChange={(o) => !o && setEditingAtt(null)}>
        <DialogContent className="bg-white rounded-none border-[#E5E5E0]">
          <DialogHeader><DialogTitle>Editar atendente</DialogTitle></DialogHeader>
          {editingAtt && (
            <div className="space-y-3">
              <div>
                <Label className="text-xs uppercase tracking-wider font-semibold">Nome</Label>
                <Input
                  value={editingAtt.name}
                  onChange={(e) => setEditingAtt({ ...editingAtt, name: e.target.value })}
                  data-testid="edit-attendant-name-input"
                  className="rounded-none mt-1"
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider font-semibold">Setores</Label>
                <div className="mt-2 border border-[#E5E5E0] max-h-48 overflow-y-auto">
                  {sectors.length === 0 ? (
                    <div className="p-3 text-xs text-[#5C5C59] text-center">Nenhum setor cadastrado.</div>
                  ) : sectors.map((s) => (
                    <label key={s.id} className="flex items-center gap-2 px-3 py-2 border-b border-[#E5E5E0] last:border-b-0 cursor-pointer hover:bg-[#F7F7F5]">
                      <input
                        type="checkbox"
                        checked={editingAtt.sector_ids.includes(s.id)}
                        onChange={() => setEditingAtt((prev) => ({
                          ...prev,
                          sector_ids: prev.sector_ids.includes(s.id)
                            ? prev.sector_ids.filter((x) => x !== s.id)
                            : [...prev.sector_ids, s.id],
                        }))}
                        data-testid={`edit-attendant-sector-${s.id}`}
                        className="accent-[#0F4C3A]"
                      />
                      <span className="inline-block w-2 h-2 rounded-full" style={{ background: s.color || "#0F4C3A" }} />
                      <span className="text-sm">{s.name}</span>
                    </label>
                  ))}
                </div>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider font-semibold">Jornada de Trabalho</Label>
                <p className="text-[10px] text-[#5C5C59] mt-0.5">
                  Restringe o login a horários específicos. Deixe em branco para acesso 24/7.
                </p>
                <Select
                  value={editingAtt.work_schedule_id || "__none__"}
                  onValueChange={(v) => setEditingAtt({ ...editingAtt, work_schedule_id: v === "__none__" ? "" : v })}
                >
                  <SelectTrigger data-testid="edit-attendant-workschedule-select" className="rounded-none mt-1 bg-white">
                    <SelectValue placeholder="Sem restrição de horário" />
                  </SelectTrigger>
                  <SelectContent className="bg-white rounded-none">
                    <SelectItem value="__none__">Sem restrição de horário</SelectItem>
                    {schedules.map((w) => (
                      <SelectItem key={w.id} value={w.id}>{w.name}{!w.active && " (inativa)"}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Permissions checkbox list (edit) */}
              <div>
                <Label className="text-xs uppercase tracking-wider font-semibold">Permissões de acesso</Label>
                <p className="text-[10px] text-[#5C5C59] mt-0.5">
                  Marque tudo que este atendente vai enxergar no painel.
                </p>
                <div className="mt-2 border border-[#E5E5E0] max-h-56 overflow-y-auto" data-testid="edit-attendant-permissions-list">
                  <label className="flex items-center gap-2 px-3 py-2 border-b border-[#E5E5E0] bg-[#F7F7F5] cursor-pointer hover:bg-[#EDF3F0]">
                    <input
                      type="checkbox"
                      checked={(editingAtt.permissions || []).length === FEATURES.length}
                      onChange={toggleAllEditPermissions}
                      data-testid="edit-attendant-all-permissions"
                      className="accent-[#0F4C3A]"
                    />
                    <span className="text-xs font-bold uppercase tracking-wider">Marcar todas</span>
                  </label>
                  {FEATURES.map((f) => (
                    <label key={f.key} className="flex items-center gap-2 px-3 py-2 border-b border-[#E5E5E0] last:border-b-0 cursor-pointer hover:bg-[#F7F7F5]">
                      <input
                        type="checkbox"
                        checked={(editingAtt.permissions || []).includes(f.key)}
                        onChange={() => toggleEditPermission(f.key)}
                        data-testid={`edit-attendant-perm-${f.key}`}
                        className="accent-[#0F4C3A]"
                      />
                      <span className="text-sm">{f.label}</span>
                    </label>
                  ))}
                </div>
                <p className="text-[10px] text-[#0F4C3A] mt-1 font-semibold">
                  {(editingAtt.permissions || []).length} de {FEATURES.length} liberado(s)
                </p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditingAtt(null)} className="rounded-none">Cancelar</Button>
            <Button onClick={saveEdit} data-testid="edit-attendant-save-btn" className="bg-[#0F4C3A] hover:bg-[#0A3629] text-white rounded-none">Salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
